import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { FileText, Send, Download, Eye, Filter, Home, Info, CalendarDays, MoreVertical, Wallet, AlertTriangle, Mail, MessageCircle } from 'lucide-react';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import axios from 'axios';
import { toast } from 'sonner';
import { QrPayDialog } from '@/components/QrPayDialog';
import { SumUpPayDialog } from '@/components/SumUpPayDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function InvoicesPage() {
  const navigate = useNavigate();

  // Rechnung → zugehöriger Termin im Kalender (öffnet Edit-Dialog via ?selected=)
  const goToAppointment = async (invoice) => {
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(`${API}/invoices/${invoice.id}/appointment-link`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      navigate(`/appointments?date=${r.data.date}&selected=${r.data.appointment_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Termin nicht gefunden');
    }
  };

  // Nur Rechnungen mit verknüpftem Termin zeigen den Kalender-Button
  const hasLinkedAppointment = (invoice) =>
    Boolean(invoice.appointment_id) ||
    (invoice.line_items || []).some((li) => li.appointment_id);

  const [invoices, setInvoices] = useState([]);
  const [rentInvoices, setRentInvoices] = useState([]);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [serverPagination, setServerPagination] = useState(false);
  const [serverTotal, setServerTotal] = useState(0);
  const [sortField, setSortField] = useState('date');
  const [sortDir, setSortDir] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 50;
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all'); // 'all', 'service', 'rent'
  const [categoryFilter, setCategoryFilter] = useState('all'); // all|training|course|rent|product
  // Deep-Link (06/2026): /invoices?search=RG-123 — z.B. aus dem
  // Rechnungs-Details-Dialog im Kundendetails-Konto-Tab ("Zur Rechnung")
  const [searchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [resendingChannel, setResendingChannel] = useState(null);

  // Rechnung erneut senden (E-Mail/WhatsApp) + Zustell-Protokoll aktualisieren
  const resendInvoiceDelivery = async (invoice, channel) => {
    if (!invoice?.id) return;
    setResendingChannel(channel);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.post(
        `${API}/invoices/${invoice.id}/dispatch`,
        { channels: [channel] },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const ch = data?.channels?.[channel] || {};
      if (ch.sent) {
        toast.success(`${channel === 'email' ? 'E-Mail' : 'WhatsApp'} erfolgreich gesendet`);
      } else {
        toast.error(`${channel === 'email' ? 'E-Mail' : 'WhatsApp'} nicht gesendet: ${ch.reason || 'unbekannt'}`);
      }
      // Zustell-Protokoll lokal ergänzen, damit der Status sofort sichtbar ist
      const entry = {
        ts: new Date().toISOString(),
        trigger: 'manual_resend',
        channels: { [channel]: { sent: !!ch.sent, to: ch.to || null, reason: ch.reason || null } },
      };
      setSelectedInvoice((prev) => prev ? { ...prev, dispatch_log: [...(prev.dispatch_log || []), entry] } : prev);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Senden fehlgeschlagen');
    } finally {
      setResendingChannel(null);
    }
  };

  // Letzten bekannten Zustellstatus je Kanal aus dispatch_log ableiten
  const latestDelivery = (invoice, channel) => {
    const log = invoice?.dispatch_log || [];
    for (let i = log.length - 1; i >= 0; i--) {
      const c = log[i]?.channels?.[channel];
      if (c && (c.sent === true || c.sent === false)) {
        return { ...c, ts: log[i].ts };
      }
    }
    return null;
  };
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [paymentRequestInvoice, setPaymentRequestInvoice] = useState(null);
  const [paymentRequestSending, setPaymentRequestSending] = useState(false);
  // Manuelles Bezahlen mit Auswahl der Zahlungsart (Bar/TWINT/SumUp/Karte …)
  const [markPaidInvoice, setMarkPaidInvoice] = useState(null);
  const [payMethods, setPayMethods] = useState([]);
  const [selectedPayMethod, setSelectedPayMethod] = useState('');
  const [markPaidSending, setMarkPaidSending] = useState(false);
  // QR/TWINT-Bezahlung am iPad (Payrexx-Zahlseite als QR-Code)
  const [qrInvoice, setQrInvoice] = useState(null);
  // SumUp-Kartenleser (nur wenn konfiguriert)
  const [sumupInvoice, setSumupInvoice] = useState(null);
  const [sumupConfigured, setSumupConfigured] = useState(false);
  // customer_ids mit direkt belastbarer gespeicherter Karte → steuert Anzeige „Erneut belasten"
  const [storedCardCustomers, setStoredCardCustomers] = useState(new Set());
  const [openSummary, setOpenSummary] = useState(null);
  const [deliveryCheckOpen, setDeliveryCheckOpen] = useState(false);
  const [deliveryCheck, setDeliveryCheck] = useState(null);
  const [deliveryCheckLoading, setDeliveryCheckLoading] = useState(false);

  const runDeliveryCheck = async () => {
    setDeliveryCheckOpen(true);
    setDeliveryCheckLoading(true);
    setDeliveryCheck(null);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${API}/invoices/delivery-check`, { headers: { Authorization: `Bearer ${token}` } });
      setDeliveryCheck(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Zustell-Kontrolle fehlgeschlagen');
    } finally {
      setDeliveryCheckLoading(false);
    }
  };

  useEffect(() => {
    axios.get(`${API}/invoices/stored-card-customers`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    }).then((r) => setStoredCardCustomers(new Set(r.data?.customer_ids || []))).catch(() => {});
  }, []);

  useEffect(() => {
    axios.get(`${API}/payments/sumup/config`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    }).then((r) => setSumupConfigured(!!r.data?.configured)).catch(() => {});
  }, []);

  useEffect(() => {
    const loadPayMethods = async () => {
      try {
        const token = localStorage.getItem('token');
        const { data } = await axios.get(`${API}/settings/payment-methods`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setPayMethods((data.methods || []).filter((m) => m.enabled));
      } catch (e) {
        // Fallback: Bar, damit das Bezahlen nie blockiert ist
        setPayMethods([{ key: 'bar', label: 'Bar', enabled: true }]);
      }
    };
    loadPayMethods();
  }, []);

  const openMarkPaid = (invoice) => {
    setSelectedPayMethod(payMethods[0]?.key || 'bar');
    setMarkPaidInvoice(invoice);
  };

  const confirmMarkPaid = async () => {
    if (!markPaidInvoice) return;
    setMarkPaidSending(true);
    try {
      const token = localStorage.getItem('token');
      const inv = markPaidInvoice;
      if (inv.invoice_type === 'rent') {
        await axios.post(
          `${API}/rent/invoices/${inv.id}/mark-paid?payment_method=${encodeURIComponent(selectedPayMethod)}`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        await axios.post(
          `${API}/invoices/${inv.id}/mark-paid`,
          { payment_method: selectedPayMethod },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      toast.success('Rechnung als bezahlt markiert');
      setMarkPaidInvoice(null);
      refreshInvoices();
    } catch (error) {
      console.error('Error marking invoice as paid:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Markieren als bezahlt');
    } finally {
      setMarkPaidSending(false);
    }
  };

  const sendPaymentRequest = async (channel) => {
    if (!paymentRequestInvoice) return;
    setPaymentRequestSending(true);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.post(
        `${API}/invoices/${paymentRequestInvoice.id}/request-payment?channel=${channel}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const parts = [];
      if (data.channels_sent?.email) parts.push('Email ✓');
      if (data.channels_sent?.whatsapp) parts.push('WhatsApp ✓');
      const sentMsg = parts.length ? parts.join(', ') : (channel === 'none' ? 'Link erzeugt' : 'kein Versand');
      toast.success(`Zahlungslink ${data.link_created_now ? 'erstellt' : 'gefunden'} (${sentMsg})`);
      if (data.errors?.length) toast.error(`Hinweis: ${data.errors.join(' · ')}`);
      if (channel === 'none' && data.payment_link) {
        try {
          await navigator.clipboard.writeText(data.payment_link);
          toast.success('Link in Zwischenablage kopiert');
        } catch { /* clipboard optional */ }
      }
      setPaymentRequestInvoice(null);
      refreshInvoices();
    } catch (e) {
      toast.error('Fehler: ' + (e.response?.data?.detail || e.message));
    } finally {
      setPaymentRequestSending(false);
    }
  };

  useEffect(() => {
    fetchOpenSummary();
  }, []);

  useEffect(() => {
    if (!serverPagination) {
      refreshInvoices();
    }
  }, [serverPagination]);

  useEffect(() => {
    if (!serverPagination) {
      filterInvoices();
    }
  }, [statusFilter, typeFilter, categoryFilter, searchTerm, invoices, rentInvoices, serverPagination, sortField, sortDir]);

  useEffect(() => {
    if (serverPagination) {
      fetchInvoicesServer();
    }
  }, [serverPagination, statusFilter, typeFilter, searchTerm, currentPage, sortField, sortDir]);

  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, typeFilter, searchTerm, serverPagination, sortField, sortDir]);

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Lade beide Rechnungstypen parallel
      const [serviceResponse, rentResponse] = await Promise.all([
        axios.get(`${API}/invoices`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/rent/invoices`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] }))
      ]);
      
      // Markiere Service-Rechnungen
      const serviceInvoices = serviceResponse.data.map(inv => ({
        ...inv,
        invoice_type: 'service',
        display_customer: inv.customer_name || 'Unbekannt'
      }));
      
      // Markiere Miet-Rechnungen
      const rentInvoicesData = rentResponse.data.map(inv => ({
        ...inv,
        invoice_type: 'rent',
        display_customer: inv.tenant_name || 'Unbekannt',
        customer_name: inv.tenant_name,
        total_amount: inv.amount
      }));
      
      setInvoices(serviceInvoices);
      setRentInvoices(rentInvoicesData);
    } catch (error) {
      console.error('Error fetching invoices:', error);
      toast.error('Fehler beim Laden der Rechnungen');
    } finally {
      setLoading(false);
    }
  };

  const fetchInvoicesServer = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = {
        page: currentPage,
        limit: pageSize,
        sort_field: sortField,
        sort_dir: sortDir
      };

      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      if (typeFilter !== 'all') {
        params.invoice_type = typeFilter;
      }
      if (searchTerm) {
        params.search = searchTerm;
      }

      const response = await axios.get(`${API}/invoices/combined`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });

      setFilteredInvoices(response.data.items || []);
      setServerTotal(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching invoices (server):', error);
      toast.error('Fehler beim Laden der Rechnungen');
    } finally {
      setLoading(false);
    }
  };

  const refreshInvoices = () => {
    if (serverPagination) {
      fetchInvoicesServer();
    } else {
      fetchInvoices();
    }
    fetchOpenSummary();
  };

  const fetchOpenSummary = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/invoices/open-summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOpenSummary(res.data);
    } catch (error) {
      console.error('Error fetching open summary:', error);
    }
  };

  const filterInvoices = () => {
    // Kombiniere beide Rechnungstypen
    let combined = [...invoices, ...rentInvoices];

    // Filter nach Typ
    if (typeFilter === 'service') {
      combined = combined.filter(inv => inv.invoice_type === 'service');
    } else if (typeFilter === 'rent') {
      combined = combined.filter(inv => inv.invoice_type === 'rent');
    }

    // Kategorie-Filter (Training / Kurs / Miete / Produkt)
    if (categoryFilter !== 'all') {
      if (categoryFilter === 'rent') {
        combined = combined.filter(inv => inv.invoice_type === 'rent');
      } else {
        combined = combined.filter(inv => inv.category === categoryFilter);
      }
    }

    let filtered = combined;

    // Status filter
    // Status filter ('open' = alle unbezahlten: pending + sent + overdue)
    if (statusFilter === 'open') {
      filtered = filtered.filter(inv => ['pending', 'sent', 'overdue'].includes(inv.status));
    } else if (statusFilter !== 'all') {
      filtered = filtered.filter(inv => inv.status === statusFilter);
    }

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(inv =>
        inv.invoice_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (inv.customer_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (inv.customer_email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (inv.display_customer || '').toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    const sorted = [...filtered].sort((a, b) => {
      const dir = sortDir === 'desc' ? -1 : 1;
      if (sortField === 'amount') {
        const aValue = a.total || a.total_amount || a.amount || 0;
        const bValue = b.total || b.total_amount || b.amount || 0;
        return dir * (aValue - bValue);
      }
      if (sortField === 'status') {
        return dir * (a.status || '').localeCompare(b.status || '');
      }
      if (sortField === 'customer') {
        const aValue = a.display_customer || a.customer_name || '';
        const bValue = b.display_customer || b.customer_name || '';
        return dir * aValue.localeCompare(bValue);
      }

      const aDate = new Date(a.invoice_date || a.created_at || 0).getTime();
      const bDate = new Date(b.invoice_date || b.created_at || 0).getTime();
      return dir * (aDate - bDate);
    });

    setFilteredInvoices(sorted);
    setCurrentPage(1);
  };

  const handleViewDetails = (invoice) => {
    setSelectedInvoice(invoice);
    setDetailDialogOpen(true);
  };

  const handleSendInvoice = async (invoiceId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/invoices/${invoiceId}/send`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Asynchroner Versand - sofortiges Feedback
      if (response.data.status === 'processing') {
        toast.success('📧 Rechnung wird im Hintergrund versendet...', {
          duration: 4000,
          icon: '🚀'
        });
        // Kein automatischer Reload - User arbeitet weiter ohne Unterbrechung
      } else {
        toast.success('Rechnung erfolgreich per Email versendet!');
        refreshInvoices();
      }
    } catch (error) {
      console.error('Error sending invoice:', error);
      const errorMessage = error.response?.data?.detail || 'Fehler beim Versenden der Rechnung';
      
      if (errorMessage.includes('SMTP nicht konfiguriert')) {
        toast.error('SMTP nicht konfiguriert! Bitte konfigurieren Sie Ihre Email-Einstellungen unter: Einstellungen → SMTP');
      } else {
        toast.error(errorMessage);
      }
    }
  };

  const handleSendReminder = async (invoiceId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/invoices/${invoiceId}/send-reminder`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Asynchroner Versand - sofortiges Feedback
      if (response.data.status === 'processing') {
        toast.success('⏰ Zahlungserinnerung wird im Hintergrund versendet...', {
          duration: 4000,
          icon: '🚀'
        });
        // Kein automatischer Reload - User arbeitet weiter ohne Unterbrechung
      } else {
        toast.success('Zahlungserinnerung gesendet');
        refreshInvoices();
      }
    } catch (error) {
      console.error('Error sending reminder:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Senden der Zahlungserinnerung');
    }
  };

  const handleSendAutomaticReminders = async () => {
    if (!window.confirm('Automatische Erinnerungen für alle fälligen Rechnungen senden? Dies basiert auf Ihren konfigurierten Erinnerungs-Regeln (7 Tage, 14 Tage, etc.).')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/reminder/send-auto`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success(response.data.message);
        refreshInvoices(); // Reload invoices to show updated reminder counts
      } else {
        toast.error(response.data.message || 'Fehler beim Senden der automatischen Erinnerungen');
      }
    } catch (error) {
      console.error('Error sending automatic reminders:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Senden der automatischen Erinnerungen');
    }
  };


  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: 'Ausstehend', color: 'bg-yellow-100 text-yellow-800' },
      sent: { label: 'Versendet', color: 'bg-blue-100 text-blue-800' },
      paid: { label: 'Bezahlt', color: 'bg-green-100 text-green-800' },
      overdue: { label: 'Überfällig', color: 'bg-red-100 text-red-800' },
      cancelled: { label: 'Storniert', color: 'bg-gray-100 text-gray-500 line-through' },
      refunded: { label: 'Rückerstattet', color: 'bg-purple-100 text-purple-800' }
    };

    const config = statusConfig[status] || statusConfig.pending;
    return (
      <Badge className={config.color}>
        {config.label}
      </Badge>
    );
  };

  // 08/2026: Zahlungsart überall sichtbar (nicht erst im PDF)
  const formatPaymentMethod = (inv) => {
    const labels = {
      guthaben: '💰 Guthaben',
      credit_card: '💳 Kreditkarte',
      creditcard: '💳 Kreditkarte',
      karte: '💳 Kreditkarte',
      karte_vor_ort: '💳 Karte vor Ort',
      kreditkarte: '💳 Kreditkarte',
      twint: '📱 TWINT',
      twint_vor_ort: '📱 TWINT vor Ort',
      sumup: '💳 SumUp',
      sumup_vor_ort: '💳 SumUp vor Ort',
      terminal: '💳 Karte (Terminal)',
      bar: '💵 Bar',
      cash: '💵 Bar',
      rechnung: '🧾 Überweisung/Rechnung',
      bank_transfer: '🏦 Überweisung',
      payrexx: '💳 Online (Payrexx)',
    };
    let label = labels[(inv.payment_method || '').toLowerCase()] || (inv.payment_method ? `Zahlungsart: ${inv.payment_method}` : '');
    if ((inv.credit_applied || 0) > 0 && (inv.payment_method || '').toLowerCase() !== 'guthaben') {
      label = label ? `💰 Guthaben + ${label.replace(/^..\s/, '')}` : '💰 Guthaben (teilweise)';
    }
    if (inv.paid_with_card_owner) {
      label += ` — Karte von ${inv.paid_with_card_owner}`;
    }
    return label ? `Bezahlt per: ${label}` : '';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('de-CH', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return `CHF ${(Number(amount) || 0).toFixed(2)}`;
  };

  const totalItems = serverPagination ? serverTotal : filteredInvoices.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const paginatedInvoices = filteredInvoices.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const displayInvoices = serverPagination ? filteredInvoices : paginatedInvoices;

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center">Lädt...</div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Rechnungen</h1>
          <p className="text-gray-600 mt-1">Verwalten Sie alle Rechnungen</p>
        </div>
      </div>

      {openSummary && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="invoices-open-summary">
          <Card
            className="border-l-4 border-l-amber-500 cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => setStatusFilter('open')}
            data-testid="invoices-open-receivables-card"
          >
            <CardContent className="p-5 flex items-center gap-4">
              <div className="rounded-full bg-amber-100 p-3">
                <Wallet className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Offene Forderungen</p>
                <p className="text-2xl font-bold text-gray-900" data-testid="invoices-open-total">
                  {formatCurrency(openSummary.open_total)}
                </p>
                <p className="text-xs text-gray-500">
                  {openSummary.open_count} unbezahlte Rechnung{openSummary.open_count === 1 ? '' : 'en'}
                </p>
              </div>
            </CardContent>
          </Card>
          <Card
            className="border-l-4 border-l-red-500 cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => setStatusFilter('overdue')}
            data-testid="invoices-overdue-card"
          >
            <CardContent className="p-5 flex items-center gap-4">
              <div className="rounded-full bg-red-100 p-3">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Davon überfällig</p>
                <p className="text-2xl font-bold text-gray-900" data-testid="invoices-overdue-total">
                  {formatCurrency(openSummary.overdue_total)}
                </p>
                <p className="text-xs text-gray-500">
                  {openSummary.overdue_count} überfällige Rechnung{openSummary.overdue_count === 1 ? '' : 'en'}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Zustell-Kontrolle Dialog */}
      <Dialog open={deliveryCheckOpen} onOpenChange={setDeliveryCheckOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="delivery-check-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5" /> Zustell-Kontrolle offener Rechnungen
            </DialogTitle>
          </DialogHeader>
          {deliveryCheckLoading ? (
            <div className="py-10 text-center text-gray-500" data-testid="delivery-check-loading">Prüfe offene Rechnungen…</div>
          ) : deliveryCheck ? (
            <div className="space-y-4">
              <p className="text-sm text-gray-500">
                Es werden nur echte Fehlversuche gemeldet – die Kundenpräferenz (E-Mail/WhatsApp) wird respektiert.
                {deliveryCheck.generated_at && <> · Stand: {new Date(deliveryCheck.generated_at).toLocaleString('de-CH', { timeZone: 'Europe/Zurich', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</>}
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <div className="rounded-lg bg-gray-100 p-3 text-center"><div className="text-xl font-bold" data-testid="dc-total">{deliveryCheck.stats.total_open}</div><div className="text-xs text-gray-600">offen gesamt</div></div>
                <div className="rounded-lg bg-green-100 p-3 text-center"><div className="text-xl font-bold text-green-700" data-testid="dc-ok">{deliveryCheck.stats.ok}</div><div className="text-xs text-gray-600">zugestellt</div></div>
                <div className="rounded-lg bg-red-100 p-3 text-center"><div className="text-xl font-bold text-red-700" data-testid="dc-problem">{deliveryCheck.stats.problem}</div><div className="text-xs text-gray-600">nicht zugestellt</div></div>
                <div className="rounded-lg bg-yellow-100 p-3 text-center"><div className="text-xl font-bold text-yellow-700" data-testid="dc-warning">{deliveryCheck.stats.warning}</div><div className="text-xs text-gray-600">Kanal-Fehler</div></div>
              </div>
              <div className="text-sm text-gray-600 flex flex-wrap gap-x-4 gap-y-1">
                <span className="flex items-center gap-1"><Mail className="h-3.5 w-3.5" /> {deliveryCheck.stats.email_sent} gesendet / <span className="text-red-600">{deliveryCheck.stats.email_failed} fehlgeschlagen</span></span>
                <span className="flex items-center gap-1"><MessageCircle className="h-3.5 w-3.5" /> {deliveryCheck.stats.whatsapp_sent} gesendet / <span className="text-red-600">{deliveryCheck.stats.whatsapp_failed} fehlgeschlagen</span></span>
              </div>

              {deliveryCheck.problems.length === 0 && deliveryCheck.warnings.length === 0 && (
                <div className="rounded-lg bg-green-50 border border-green-200 p-4 text-green-800 font-medium" data-testid="dc-all-good">
                  ✅ Alle offenen Rechnungen wurden erfolgreich zugestellt.
                </div>
              )}

              {deliveryCheck.problems.length > 0 && (
                <div data-testid="dc-problems">
                  <h4 className="font-semibold text-red-700 mb-2">Nicht zugestellt ({deliveryCheck.problems.length})</h4>
                  <div className="space-y-1.5">
                    {deliveryCheck.problems.map((p) => (
                      <div key={p.invoice_id} className="rounded-md border border-red-200 bg-red-50 p-2 text-sm" data-testid={`dc-problem-${p.invoice_number}`}>
                        <div className="flex justify-between gap-2">
                          <span className="font-medium">{p.invoice_number} · {p.customer_name}</span>
                          <span className="text-gray-600 whitespace-nowrap">{formatCurrency(p.amount)} · {p.status_label}</span>
                        </div>
                        <div className="text-red-700 text-xs mt-0.5">{p.reason}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {deliveryCheck.warnings.length > 0 && (
                <div data-testid="dc-warnings">
                  <h4 className="font-semibold text-yellow-700 mb-2">Zugestellt, aber ein Kanal fehlgeschlagen ({deliveryCheck.warnings.length})</h4>
                  <div className="space-y-1.5">
                    {deliveryCheck.warnings.map((p) => (
                      <div key={p.invoice_id} className="rounded-md border border-yellow-200 bg-yellow-50 p-2 text-sm">
                        <div className="flex justify-between gap-2">
                          <span className="font-medium">{p.invoice_number} · {p.customer_name}</span>
                          <span className="text-gray-600 whitespace-nowrap">{formatCurrency(p.amount)} · zugestellt via {p.delivered_via}</span>
                        </div>
                        <div className="text-yellow-700 text-xs mt-0.5">{p.failed.map((f) => `${f.channel}: ${f.reason}`).join(' · ')}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="py-10 text-center text-gray-500">Keine Daten.</div>
          )}
        </DialogContent>
      </Dialog>

      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Alle Rechnungen ({totalItems})
            </CardTitle>
            <div className="flex gap-3 w-full sm:w-auto items-center justify-end flex-wrap">
              <Button
                variant="default"
                size="sm"
                onClick={handleSendAutomaticReminders}
                className="bg-orange-600 hover:bg-orange-700"
              >
                ⏰ Automatische Erinnerungen senden
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={runDeliveryCheck}
                data-testid="delivery-check-btn"
                className="border-emerald-600 text-emerald-700 hover:bg-emerald-50"
              >
                <Mail className="w-4 h-4 mr-1" /> Zustell-Kontrolle
              </Button>
              <div className="flex items-center gap-2" data-testid="invoices-server-pagination-toggle">
                <Switch
                  checked={serverPagination}
                  onCheckedChange={setServerPagination}
                  data-testid="invoices-server-pagination-switch"
                />
                <span className="text-sm text-gray-600">Server Pagination</span>
                <TooltipProvider delayDuration={200}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button type="button" aria-label="Server Pagination Info" data-testid="invoices-server-pagination-info">
                        <Info className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-xs text-left">
                      Lädt Daten schrittweise vom Server, um die Performance bei vielen Einträgen zu verbessern. Aus: Alle Daten werden auf einmal geladen (für kleine Listen).
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <Select value={sortField} onValueChange={setSortField}>
                <SelectTrigger className="w-[160px]" data-testid="invoices-sort-field-select">
                  <SelectValue placeholder="Sortierung" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="date">Datum</SelectItem>
                  <SelectItem value="amount">Betrag</SelectItem>
                  <SelectItem value="status">Status</SelectItem>
                  <SelectItem value="customer">Kunde</SelectItem>
                </SelectContent>
              </Select>
              <Select value={sortDir} onValueChange={setSortDir}>
                <SelectTrigger className="w-[150px]" data-testid="invoices-sort-dir-select">
                  <SelectValue placeholder="Richtung" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="desc">Absteigend</SelectItem>
                  <SelectItem value="asc">Aufsteigend</SelectItem>
                </SelectContent>
              </Select>
              <div className="flex-1 sm:flex-none">
                <Input
                  placeholder="Suchen..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full sm:w-48"
                />
              </div>
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="Typ" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Typen</SelectItem>
                  <SelectItem value="service">📋 Service</SelectItem>
                  <SelectItem value="rent">🏠 Miete</SelectItem>
                </SelectContent>
              </Select>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-[150px]" data-testid="invoice-category-filter">
                  <SelectValue placeholder="Kategorie" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Kategorien</SelectItem>
                  <SelectItem value="training">🏋️ Training</SelectItem>
                  <SelectItem value="course">🧘 Kurs</SelectItem>
                  <SelectItem value="rent">🏠 Miete</SelectItem>
                  <SelectItem value="product">🛒 Produkt</SelectItem>
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Status</SelectItem>
                  <SelectItem value="open">💰 Offen (unbezahlt)</SelectItem>
                  <SelectItem value="pending">Ausstehend</SelectItem>
                  <SelectItem value="sent">Versendet</SelectItem>
                  <SelectItem value="paid">Bezahlt</SelectItem>
                  <SelectItem value="overdue">Überfällig</SelectItem>
                  <SelectItem value="cancelled">Storniert</SelectItem>
                  <SelectItem value="refunded">Rückerstattet</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Typ</TableHead>
                  <TableHead>Rechnungsnr.</TableHead>
                  <TableHead>Kunde/Mieter</TableHead>
                  <TableHead>Verkäufer</TableHead>
                  <TableHead>Datum</TableHead>
                  <TableHead>Fällig am</TableHead>
                  <TableHead className="text-right">Betrag</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Erinnerungen</TableHead>
                  <TableHead className="text-right">Aktionen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {displayInvoices.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center text-gray-500 py-8">
                      Keine Rechnungen gefunden
                    </TableCell>
                  </TableRow>
                ) : (
                  displayInvoices.map((invoice) => (
                    <TableRow key={invoice.id}>
                      <TableCell>
                        {invoice.invoice_type === 'rent' ? (
                          <Badge className="bg-purple-100 text-purple-800 flex items-center gap-1 w-fit">
                            <Home size={12} />
                            Miete
                          </Badge>
                        ) : (
                          <Badge className="bg-blue-100 text-blue-800 flex items-center gap-1 w-fit">
                            <FileText size={12} />
                            Service
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="font-medium">
                        {invoice.invoice_number}
                      </TableCell>
                      <TableCell>
                        <div>
                          <div className="font-medium">{invoice.display_customer || invoice.customer_name}</div>
                          <div className="text-sm text-gray-500">
                            {invoice.invoice_type === 'rent' ? invoice.rented_room : invoice.customer_email}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          {invoice.sold_by_name || '-'}
                        </div>
                      </TableCell>
                      <TableCell>{formatDate(invoice.invoice_date || invoice.created_at)}</TableCell>
                      <TableCell>{formatDate(invoice.due_date)}</TableCell>
                      <TableCell className="text-right font-semibold">
                        {formatCurrency(invoice.total || invoice.total_amount || invoice.amount || 0)}
                      </TableCell>
                      <TableCell>
                        <div className="space-y-0.5">
                          {getStatusBadge(invoice.status)}
                          {invoice.status === 'paid' && invoice.paid_at && (
                            <div className="text-[11px] text-green-700" data-testid={`invoice-paid-at-${invoice.id}`}>
                              {new Date(invoice.paid_at).toLocaleString('de-CH', { timeZone: 'Europe/Zurich', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            </div>
                          )}
                          {invoice.status === 'paid' && (invoice.payment_method || invoice.credit_applied > 0) && (
                            <div className="text-[11px] text-gray-600" data-testid={`invoice-paid-method-${invoice.id}`}>
                              {formatPaymentMethod(invoice)}
                            </div>
                          )}
                          {invoice.status !== 'cancelled' && (() => {
                            const em = latestDelivery(invoice, 'email');
                            const wa = latestDelivery(invoice, 'whatsapp');
                            const fmt = (ts) => ts ? new Date(ts).toLocaleString('de-CH', { timeZone: 'Europe/Zurich', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '';
                            if (em || wa) {
                              return (
                                <div className="space-y-0.5 pt-0.5" data-testid={`invoice-delivery-mini-${invoice.id}`}>
                                  {em && (
                                    <div className={`text-[11px] flex items-center gap-1 ${em.sent ? 'text-green-700' : 'text-red-600'}`} title={em.sent ? `E-Mail gesendet${em.to ? ' an ' + em.to : ''}` : `E-Mail nicht gesendet${em.reason ? ': ' + em.reason : ''}`} data-testid={`invoice-delivery-email-${invoice.id}`}>
                                      <Mail className="h-3 w-3 shrink-0" />
                                      <span>{em.sent ? fmt(em.ts) : 'fehlgeschlagen'}</span>
                                    </div>
                                  )}
                                  {wa && (
                                    <div className={`text-[11px] flex items-center gap-1 ${wa.sent ? 'text-green-700' : 'text-red-600'}`} title={wa.sent ? `WhatsApp gesendet${wa.to ? ' an ' + wa.to : ''}` : `WhatsApp nicht gesendet${wa.reason ? ': ' + wa.reason : ''}`} data-testid={`invoice-delivery-whatsapp-${invoice.id}`}>
                                      <MessageCircle className="h-3 w-3 shrink-0" />
                                      <span>{wa.sent ? fmt(wa.ts) : 'fehlgeschlagen'}</span>
                                    </div>
                                  )}
                                </div>
                              );
                            }
                            if (invoice.sent_at && invoice.status !== 'paid') {
                              return (
                                <div className="text-[11px] text-gray-500 pt-0.5" data-testid={`invoice-sent-at-${invoice.id}`}>
                                  Versendet: {new Date(invoice.sent_at).toLocaleString('de-CH', { timeZone: 'Europe/Zurich', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                </div>
                              );
                            }
                            return null;
                          })()}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          {invoice.reminder_count > 0 ? (
                            <TooltipProvider delayDuration={200}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className="space-y-1 cursor-help" data-testid={`invoice-reminder-log-${invoice.id}`}>
                                    <div className="font-medium flex items-center gap-1">
                                      {invoice.reminder_count}x gesendet
                                      <Info className="h-3 w-3 text-gray-400" />
                                    </div>
                                    {invoice.last_reminder_sent_at && (
                                      <div className="text-xs text-gray-500">
                                        Letzte: {new Date(invoice.last_reminder_sent_at).toLocaleString('de-CH', { timeZone: 'Europe/Zurich', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                      </div>
                                    )}
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent side="left" className="max-w-xs text-left">
                                  <div className="text-xs font-semibold mb-1">Mahnungsverlauf</div>
                                  {Array.isArray(invoice.reminders_log) && invoice.reminders_log.length > 0 ? (
                                    <ul className="space-y-1 text-xs">
                                      {invoice.reminders_log.slice(-5).map((r, i) => (
                                        <li key={i}>
                                          {new Date(r.sent_at).toLocaleString('de-DE')} • {r.trigger === 'auto' ? 'Auto' : 'Manuell'}
                                          {r.channels?.email ? ' • E-Mail' : ''}
                                          {r.channels?.whatsapp ? ' • WhatsApp' : ''}
                                        </li>
                                      ))}
                                    </ul>
                                  ) : (
                                    <div className="text-xs">Detailprotokoll wird ab nächster Mahnung erfasst.</div>
                                  )}
                                  <div className="text-[10px] text-gray-300 mt-1">Auto-Mahnungen: alle 5 Tage</div>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              size="sm"
                              variant="outline"
                              className="px-2"
                              data-testid={`invoice-actions-${invoice.id}`}
                            >
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-56">
                          {/* Miet-Rechnungen: Vereinfachte Aktionen */}
                          {invoice.invoice_type === 'rent' ? (
                            <>
                              {invoice.status !== 'paid' && (
                                <>
                                  <DropdownMenuItem
                                    data-testid={`rent-invoice-send-${invoice.id}`}
                                    onClick={async () => {
                                      try {
                                        const token = localStorage.getItem('token');
                                        await axios.post(
                                          `${API}/rent/invoices/${invoice.id}/resend`,
                                          {},
                                          { headers: { Authorization: `Bearer ${token}` } }
                                        );
                                        toast.success('Mietrechnung erneut versendet');
                                        refreshInvoices();
                                      } catch (error) {
                                        toast.error('Fehler beim Versenden');
                                      }
                                    }}
                                  >
                                    <Send className="w-4 h-4 mr-2" />
                                    Senden
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    className="text-green-600"
                                    data-testid={`rent-invoice-mark-paid-${invoice.id}`}
                                    onClick={() => openMarkPaid(invoice)}
                                  >
                                    Bezahlt
                                  </DropdownMenuItem>
                                </>
                              )}
                              {/* Rückerstatten für bezahlte Miet-Rechnungen */}
                              {invoice.status === 'paid' && (
                                <DropdownMenuItem
                                  className="text-purple-600"
                                  data-testid={`rent-invoice-refund-${invoice.id}`}
                                  onClick={async () => {
                                    if (!window.confirm(`Miet-Rechnung ${invoice.invoice_number} wirklich rückerstatten?`)) return;
                                    try {
                                      const token = localStorage.getItem('token');
                                      await axios.post(
                                        `${API}/rent/invoices/${invoice.id}/refund`,
                                        {},
                                        { headers: { Authorization: `Bearer ${token}` } }
                                      );
                                      toast.success('Rückerstattet');
                                      refreshInvoices();
                                    } catch (error) {
                                      toast.error(error.response?.data?.detail || 'Fehler');
                                    }
                                  }}
                                >
                                  Rückerstatten
                                </DropdownMenuItem>
                              )}
                              {/* PDF Download für Miet-Rechnungen */}
                              <DropdownMenuItem
                                data-testid={`rent-invoice-download-${invoice.id}`}
                                onClick={async () => {
                                  try {
                                    const token = localStorage.getItem('token');
                                    const response = await axios.get(
                                      `${API}/rent/invoices/${invoice.id}/pdf`,
                                      {
                                        headers: { Authorization: `Bearer ${token}` },
                                        responseType: 'blob'
                                      }
                                    );
                                    const blob = new Blob([response.data], { type: 'application/pdf' });
                                    const url = window.URL.createObjectURL(blob);
                                    const link = document.createElement('a');
                                    link.href = url;
                                    link.download = `Mietrechnung_${invoice.invoice_number}.pdf`;
                                    document.body.appendChild(link);
                                    link.click();
                                    link.remove();
                                    window.URL.revokeObjectURL(url);
                                  } catch (error) {
                                    toast.error('Fehler beim PDF-Download');
                                  }
                                }}
                              >
                                <Download className="w-4 h-4 mr-2" />
                                PDF herunterladen
                              </DropdownMenuItem>
                              {/* Löschen für Miet-Rechnungen */}
                              <DropdownMenuItem
                                className="text-red-600"
                                data-testid={`rent-invoice-delete-${invoice.id}`}
                                onClick={async () => {
                                  if (!window.confirm(`⚠️ Miet-Rechnung ${invoice.invoice_number} wirklich LÖSCHEN?`)) return;
                                  try {
                                    const token = localStorage.getItem('token');
                                    await axios.delete(
                                      `${API}/rent/invoices/${invoice.id}`,
                                      { headers: { Authorization: `Bearer ${token}` } }
                                    );
                                    toast.success('Gelöscht');
                                    refreshInvoices();
                                  } catch (error) {
                                    toast.error(error.response?.data?.detail || 'Fehler');
                                  }
                                }}
                              >
                                🗑️ Löschen
                              </DropdownMenuItem>
                            </>
                          ) : (
                            <>
                              {/* Service-Rechnungen: Alle Aktionen */}
                              <DropdownMenuItem
                                data-testid={`invoice-view-${invoice.id}`}
                                onClick={() => handleViewDetails(invoice)}
                              >
                                <Eye className="w-4 h-4 mr-2" />
                                Details ansehen
                              </DropdownMenuItem>
                              {hasLinkedAppointment(invoice) && (
                                <DropdownMenuItem
                                  data-testid={`invoice-goto-appointment-${invoice.id}`}
                                  onClick={() => goToAppointment(invoice)}
                                >
                                  <CalendarDays className="w-4 h-4 mr-2" />
                                  Zum Termin springen
                                </DropdownMenuItem>
                              )}
                              {invoice.status === 'pending' && (
                                <DropdownMenuItem
                                  data-testid={`invoice-send-${invoice.id}`}
                                  onClick={() => handleSendInvoice(invoice.id)}
                                >
                                  <Send className="w-4 h-4 mr-2" />
                                  Rechnung senden
                                </DropdownMenuItem>
                              )}
                              {invoice.status !== 'paid' && invoice.status !== 'cancelled' && invoice.status !== 'refunded' && (
                            <DropdownMenuItem
                              data-testid={`invoice-reminder-${invoice.id}`}
                              onClick={() => handleSendReminder(invoice.id)}
                            >
                              <Send className="w-4 h-4 mr-2" />
                              Erinnerung
                            </DropdownMenuItem>
                          )}
                          {invoice.status !== 'paid' && invoice.status !== 'cancelled' && invoice.status !== 'refunded' && storedCardCustomers.has(invoice.customer_id) && (
                            <DropdownMenuItem
                              data-testid={`invoice-retry-charge-${invoice.id}`}
                              onClick={async () => {
                                if (!window.confirm('Versucht jetzt, die Rechnung über die hinterlegte Kreditkarte zu belasten. Fortfahren?')) return;
                                try {
                                  const token = localStorage.getItem('token');
                                  const r = await axios.post(
                                    `${API}/invoices/${invoice.id}/retry-charge`,
                                    {},
                                    { headers: { Authorization: `Bearer ${token}` } }
                                  );
                                  if (r.data?.success) {
                                    toast.success(r.data.message || 'Zahlung erfolgreich');
                                  } else {
                                    toast.error(r.data?.message || 'Belastung fehlgeschlagen');
                                  }
                                  refreshInvoices();
                                } catch (error) {
                                  console.error('Retry charge failed:', error);
                                  toast.error(error.response?.data?.detail || 'Fehler beim Erneut-Belasten');
                                }
                              }}
                            >
                              🔄 Erneut belasten
                            </DropdownMenuItem>
                          )}
                          {invoice.status !== 'paid' && invoice.status !== 'cancelled' && invoice.status !== 'refunded' && (
                            <DropdownMenuItem
                              className="text-yellow-700"
                              data-testid={`invoice-request-payment-${invoice.id}`}
                              onClick={() => setPaymentRequestInvoice(invoice)}
                            >
                              💳 Zahlung anfordern
                            </DropdownMenuItem>
                          )}
                          {invoice.status !== 'paid' && invoice.status !== 'cancelled' && invoice.status !== 'refunded' && (
                            <DropdownMenuItem
                              className="text-blue-700"
                              data-testid={`invoice-qr-pay-${invoice.id}`}
                              onClick={() => setQrInvoice(invoice)}
                            >
                              📱 TWINT / QR
                            </DropdownMenuItem>
                          )}
                          {sumupConfigured && invoice.status !== 'paid' && invoice.status !== 'cancelled' && invoice.status !== 'refunded' && (
                            <DropdownMenuItem
                              className="text-purple-700"
                              data-testid={`invoice-sumup-pay-${invoice.id}`}
                              onClick={() => setSumupInvoice(invoice)}
                            >
                              💳 SumUp Terminal
                            </DropdownMenuItem>
                          )}
                          {invoice.status !== 'paid' && invoice.status !== 'cancelled' && invoice.status !== 'refunded' && (
                            <DropdownMenuItem
                              data-testid={`invoice-mark-paid-${invoice.id}`}
                              onClick={() => openMarkPaid(invoice)}
                            >
                              Als bezahlt markieren
                            </DropdownMenuItem>
                          )}
                          {/* Zahlung korrigieren — direkt auf der Rechnungsseite
                              (User-Wunsch 06/2026) */}
                          {invoice.status === 'paid' && (
                            <>
                              <DropdownMenuItem
                                className="text-amber-700"
                                data-testid={`invoice-refund-${invoice.id}`}
                                onClick={async () => {
                                  if (!window.confirm(`Zahlung erstatten?\n\nCHF ${(invoice.total || 0).toFixed(2)} werden dem Kundenguthaben gutgeschrieben, die Rechnung wird als "erstattet" markiert und ggf. Punkte/Abo zurückgebucht.`)) return;
                                  try {
                                    const token = localStorage.getItem('token');
                                    const r = await axios.post(
                                      `${API}/invoices/${invoice.id}/refund-payment`,
                                      {},
                                      { headers: { Authorization: `Bearer ${token}` } }
                                    );
                                    toast.success(r.data?.message || 'Zahlung erstattet');
                                    refreshInvoices();
                                  } catch (error) {
                                    toast.error(error.response?.data?.detail || 'Fehler beim Erstatten');
                                  }
                                }}
                              >
                                ↩️ Erstatten
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                className="text-rose-700"
                                data-testid={`invoice-unpay-${invoice.id}`}
                                onClick={async () => {
                                  if (!window.confirm('Zahlung löschen?\n\nDie Rechnung wird wieder als "offen" markiert und ggf. gutgeschriebene Punkte werden zurückgebucht.')) return;
                                  try {
                                    const token = localStorage.getItem('token');
                                    const r = await axios.post(
                                      `${API}/invoices/${invoice.id}/unpay`,
                                      {},
                                      { headers: { Authorization: `Bearer ${token}` } }
                                    );
                                    toast.success(r.data?.message || 'Zahlung gelöscht');
                                    refreshInvoices();
                                  } catch (error) {
                                    toast.error(error.response?.data?.detail || 'Fehler beim Löschen der Zahlung');
                                  }
                                }}
                              >
                                Zahlung löschen
                              </DropdownMenuItem>
                            </>
                          )}
                          {invoice.status === 'refunded' && (
                            <DropdownMenuItem
                              className="text-emerald-700"
                              data-testid={`invoice-undo-refund-${invoice.id}`}
                              onClick={async () => {
                                if (!window.confirm(`Erstattung zurückziehen?\n\nCHF ${(invoice.total || 0).toFixed(2)} werden vom Kundenguthaben abgezogen, die Rechnung wird wieder als "bezahlt" markiert und Punkte/Abo werden wiederhergestellt.`)) return;
                                try {
                                  const token = localStorage.getItem('token');
                                  const r = await axios.post(
                                    `${API}/invoices/${invoice.id}/undo-refund`,
                                    {},
                                    { headers: { Authorization: `Bearer ${token}` } }
                                  );
                                  toast.success(r.data?.message || 'Erstattung zurückgezogen');
                                  refreshInvoices();
                                } catch (error) {
                                  toast.error(error.response?.data?.detail || 'Fehler beim Zurückziehen');
                                }
                              }}
                            >
                              ↩️ Erstattung zurückziehen
                            </DropdownMenuItem>
                          )}
                          {invoice.status !== 'cancelled' && invoice.status !== 'paid' && invoice.status !== 'refunded' && (
                            <DropdownMenuItem
                              className="text-red-600"
                              data-testid={`invoice-cancel-${invoice.id}`}
                              onClick={() => {
                                if (!window.confirm('Achtung: Wenn Sie diese Rechnung stornieren, werden alle zugehörigen Punkte beim Kunden wieder abgezogen. Fortfahren?')) {
                                  return;
                                }
                                try {
                                  const token = localStorage.getItem('token');
                                  axios.post(
                                    `${API}/invoices/${invoice.id}/cancel`,
                                    {},
                                    { headers: { Authorization: `Bearer ${token}` } }
                                  ).then(() => {
                                    toast.success('Rechnung storniert und Punkte korrigiert');
                                    refreshInvoices();
                                  }).catch((error) => {
                                    console.error('Error cancelling invoice:', error);
                                    const msg = error.response?.data?.detail || 'Fehler beim Stornieren der Rechnung';
                                    toast.error(msg);
                                  });
                                } catch (error) {
                                  console.error('Error cancelling invoice:', error);
                                  toast.error('Fehler beim Stornieren der Rechnung');
                                }
                              }}
                            >
                              Stornieren
                            </DropdownMenuItem>
                          )}
                          {/* Löschen Button - nur für stornierte oder offene Rechnungen */}
                          {(invoice.status === 'cancelled' || invoice.status === 'pending') && (
                            <DropdownMenuItem
                              className="text-red-700"
                              data-testid={`invoice-delete-${invoice.id}`}
                              onClick={async () => {
                                const confirmMsg = invoice.status === 'cancelled' 
                                  ? 'Diese stornierte Rechnung komplett löschen?' 
                                  : '⚠️ ACHTUNG: Diese Rechnung komplett löschen?\n\nDies löscht auch:\n• Zugehöriges Abo (falls vorhanden)\n• Punkte-Pakete (falls vorhanden)\n• Alle Zahlungen\n\nWenn bereits Check-Ins durchgeführt wurden, ist das Löschen nicht möglich.';
                                
                                if (!window.confirm(confirmMsg)) return;
                                
                                try {
                                  const token = localStorage.getItem('token');
                                  const res = await axios.delete(
                                    `${API}/invoices/${invoice.id}`,
                                    { headers: { Authorization: `Bearer ${token}` } }
                                  );
                                  toast.success(`✅ ${res.data.message}`);
                                  refreshInvoices();
                                } catch (error) {
                                  console.error('Error deleting invoice:', error);
                                  const msg = error.response?.data?.detail || 'Fehler beim Löschen der Rechnung';
                                  // 08/2026: Bei Punkte-/Abo-Sperre „Trotzdem löschen" anbieten
                                  if (error.response?.status === 400 && /Punkt|Credit|verwendet/i.test(msg)) {
                                    if (window.confirm(`${msg}\n\n➡️ Trotzdem löschen? Verbrauchte Punkte/Credits werden dabei so weit wie möglich zurückgesetzt (kein Negativ-Saldo).`)) {
                                      try {
                                        const token2 = localStorage.getItem('token');
                                        const res2 = await axios.delete(
                                          `${API}/invoices/${invoice.id}?force=true`,
                                          { headers: { Authorization: `Bearer ${token2}` } }
                                        );
                                        toast.success(`✅ ${res2.data.message}`);
                                        refreshInvoices();
                                        return;
                                      } catch (e2) {
                                        toast.error(e2.response?.data?.detail || 'Fehler beim Löschen (force)');
                                        return;
                                      }
                                    }
                                    return;
                                  }
                                  toast.error(msg);
                                }
                              }}
                            >
                              🗑️ Löschen
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            data-testid={`invoice-download-${invoice.id}`}
                            onClick={async () => {
                              const token = localStorage.getItem('token');
                              try {
                                const res = await axios.get(
                                  `${API}/invoices/${invoice.id}/pdf`,
                                  {
                                    headers: { Authorization: `Bearer ${token}` },
                                    responseType: 'blob',
                                  }
                                );
                                const blob = new Blob([res.data], { type: 'application/pdf' });
                                const url = window.URL.createObjectURL(blob);
                                const link = document.createElement('a');
                                link.href = url;
                                link.download = `Rechnung_${invoice.invoice_number || invoice.id}.pdf`;
                                document.body.appendChild(link);
                                link.click();
                                link.remove();
                                window.URL.revokeObjectURL(url);
                              } catch (error) {
                                console.error('Fehler beim Herunterladen der Rechnung', error);
                                toast.error('Fehler beim Herunterladen der Rechnung');
                              }
                            }}
                          >
                            <Download className="w-4 h-4 mr-2" />
                            PDF herunterladen
                          </DropdownMenuItem>
                            </>
                          )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            <div className="flex items-center justify-between pt-4" data-testid="invoices-pagination">
              <span className="text-xs text-gray-500" data-testid="invoices-pagination-info">
                Seite {currentPage} von {totalPages}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  data-testid="invoices-pagination-prev"
                >
                  Zurück
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  data-testid="invoices-pagination-next"
                >
                  Weiter
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Invoice Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" aria-describedby={undefined} data-testid="invoice-detail-dialog">
          <DialogHeader>
            <DialogTitle data-testid="invoice-detail-title">Rechnung {selectedInvoice?.invoice_number}</DialogTitle>
          </DialogHeader>
          {selectedInvoice && (
            <div className="space-y-6">
              {/* Invoice Info */}
              <div className="grid grid-cols-2 gap-4" data-testid="invoice-detail-info">
                <div data-testid="invoice-detail-customer">
                  <h3 className="font-semibold mb-2">Kunde</h3>
                  <p data-testid={`invoice-detail-customer-name-${selectedInvoice.id}`}>{selectedInvoice.customer_name}</p>
                  <p className="text-sm text-gray-600" data-testid={`invoice-detail-customer-email-${selectedInvoice.id}`}>{selectedInvoice.customer_email}</p>
                  {selectedInvoice.customer_street && (
                    <>
                      <p className="text-sm text-gray-600" data-testid={`invoice-detail-customer-street-${selectedInvoice.id}`}>{selectedInvoice.customer_street}</p>
                      <p className="text-sm text-gray-600" data-testid={`invoice-detail-customer-city-${selectedInvoice.id}`}>
                        {selectedInvoice.customer_postal_code} {selectedInvoice.customer_city}
                      </p>
                    </>
                  )}
                </div>
                <div className="text-right space-y-1" data-testid="invoice-detail-meta">
                  <h3 className="font-semibold mb-2">Rechnungsdetails</h3>
                  <p className="text-sm" data-testid={`invoice-detail-date-${selectedInvoice.id}`}>Datum: {formatDate(selectedInvoice.invoice_date)}</p>
                  <p className="text-sm" data-testid={`invoice-detail-due-date-${selectedInvoice.id}`}>Fällig: {formatDate(selectedInvoice.due_date)}</p>
                  {selectedInvoice.paid_at && (
                    <p className="text-sm font-medium text-green-700" data-testid={`invoice-detail-paid-at-${selectedInvoice.id}`}>
                      ✅ Bezahlt am: {new Date(selectedInvoice.paid_at).toLocaleString('de-CH', { timeZone: 'Europe/Zurich', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })} Uhr
                    </p>
                  )}
                  {selectedInvoice.status === 'paid' && (selectedInvoice.payment_method || selectedInvoice.credit_applied > 0) && (
                    <p className="text-sm text-gray-700" data-testid="invoice-detail-paid-method">
                      {formatPaymentMethod(selectedInvoice)}
                    </p>
                  )}
                  <div className="text-sm flex items-center justify-end gap-1" data-testid={`invoice-detail-status-${selectedInvoice.id}`}>
                    Status: {getStatusBadge(selectedInvoice.status)}
                  </div>
                  {selectedInvoice.qr_reference && (
                    <p className="text-xs text-gray-600 break-all" data-testid={`invoice-detail-reference-${selectedInvoice.id}`}>
                      Referenz (RF): {selectedInvoice.qr_reference}
                    </p>
                  )}
                </div>
              </div>

              {/* Line Items */}
              <div>
                <h3 className="font-semibold mb-3">Positionen</h3>
                <Table data-testid="invoice-detail-line-items">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Datum</TableHead>
                      <TableHead>Service</TableHead>
                      <TableHead>Trainer</TableHead>
                      <TableHead className="text-right">Preis</TableHead>
                      <TableHead className="text-right">MWST</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedInvoice.line_items?.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell className="text-sm">{formatDate(item.date)}</TableCell>
                        <TableCell>
                          <div className="font-medium">{item.service_name}</div>
                          {item.beneficiary_name && (
                            <div className="text-xs font-semibold text-blue-700" data-testid={`invoice-item-beneficiary-${index}`}>
                              für {item.beneficiary_name}
                            </div>
                          )}
                          {item.discount_value > 0 && (
                            <div className="text-xs text-red-600">
                              -{item.discount_value}{item.discount_type === 'percentage' ? '%' : ' CHF'}
                            </div>
                          )}
                          {item.discount_reason === 'loyalty' && (
                            <div className="text-xs font-semibold text-emerald-700" data-testid={`invoice-item-loyalty-${index}`}>
                              🎁 Treue-Rabatt (2. Training)
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="text-sm">{item.trainer_name || '-'}</TableCell>
                        <TableCell className="text-right">
                          {item.standard_price > item.net_price + 0.005 && (
                            <span className="text-xs text-gray-400 line-through mr-1">{formatCurrency(item.standard_price)}</span>
                          )}
                          {formatCurrency(item.net_price)}
                        </TableCell>
                        <TableCell className="text-right text-sm">
                          {formatCurrency(item.tax_amount)}
                          <span className="text-gray-500 ml-1">({item.tax_rate}%)</span>
                        </TableCell>
                        <TableCell className="text-right font-semibold">
                          {formatCurrency(item.gross_price)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Totals */}
              <div className="border-t pt-4">
                <div className="flex justify-end">
                  <div className="w-64 space-y-2">
                    <div className="flex justify-between">
                      <span>Zwischensumme:</span>
                      <span data-testid={`invoice-detail-subtotal-${selectedInvoice.id}`}>{formatCurrency(selectedInvoice.subtotal)}</span>
                    </div>
                    {/* Show discount if applicable */}
                    {(selectedInvoice.discount_percent > 0 || selectedInvoice.discount_amount > 0) && (
                      <div className="flex justify-between text-sm text-green-600">
                        <span>Rabatt ({selectedInvoice.discount_percent || 0}%):</span>
                        <span data-testid={`invoice-detail-discount-${selectedInvoice.id}`}>-{formatCurrency(selectedInvoice.discount_amount || (selectedInvoice.subtotal * (selectedInvoice.discount_percent || 0) / 100))}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm text-gray-600">
                      <span>MWST:</span>
                      <span data-testid={`invoice-detail-tax-${selectedInvoice.id}`}>{formatCurrency(selectedInvoice.total_tax)}</span>
                    </div>
                    {/* 08/2026: Verrechnetes Guthaben schon im Dialog zeigen (nicht erst im PDF) */}
                    {(selectedInvoice.credit_applied || 0) > 0 && (
                      <>
                        <div className="flex justify-between text-sm">
                          <span>Total (inkl. MWST):</span>
                          <span>{formatCurrency((selectedInvoice.subtotal || 0) + (selectedInvoice.total_tax || 0))}</span>
                        </div>
                        <div className="flex justify-between text-sm text-emerald-700 font-medium">
                          <span>💰 Verrechnetes Guthaben:</span>
                          <span data-testid={`invoice-detail-credit-${selectedInvoice.id}`}>-{formatCurrency(selectedInvoice.credit_applied)}</span>
                        </div>
                      </>
                    )}
                    <div className="flex justify-between font-bold text-lg border-t pt-2">
                      <span>{(selectedInvoice.credit_applied || 0) > 0 ? 'Zu zahlen:' : 'Gesamt:'}</span>
                      <span data-testid={`invoice-detail-total-${selectedInvoice.id}`}>{formatCurrency(selectedInvoice.total)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Zustellung (Versand-Protokoll: E-Mail / WhatsApp) */}
              {selectedInvoice.status !== 'cancelled' && (
                <div data-testid="invoice-delivery-section">
                  <h3 className="font-semibold mb-2">Zustellung</h3>
                  <div className="space-y-2">
                    {['email', 'whatsapp'].map((channel) => {
                      const d = latestDelivery(selectedInvoice, channel);
                      const label = channel === 'email' ? 'E-Mail' : 'WhatsApp';
                      const sent = d?.sent === true;
                      const attempted = d !== null;
                      return (
                        <div key={channel} className="flex items-center justify-between rounded-md border p-2" data-testid={`invoice-delivery-${channel}`}>
                          <div className="flex items-start gap-2">
                            <span className={`mt-0.5 text-sm ${sent ? 'text-green-600' : attempted ? 'text-red-600' : 'text-gray-400'}`}>
                              {sent ? '✅' : attempted ? '❌' : '—'}
                            </span>
                            <div>
                              <div className="text-sm font-medium text-gray-900">{label}</div>
                              <div className="text-xs text-gray-500" data-testid={`invoice-delivery-${channel}-status`}>
                                {sent
                                  ? `Gesendet${d.to ? ` an ${d.to}` : ''}${d.ts ? ` · ${new Date(d.ts).toLocaleString('de-CH', { timeZone: 'Europe/Zurich', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}` : ''}`
                                  : attempted
                                    ? `Nicht gesendet${d.reason ? ` (${d.reason})` : ''}${d.ts ? ` · ${new Date(d.ts).toLocaleString('de-CH', { timeZone: 'Europe/Zurich', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}` : ''}`
                                    : 'Noch kein Versand protokolliert'}
                              </div>
                            </div>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={resendingChannel === channel}
                            onClick={() => resendInvoiceDelivery(selectedInvoice, channel)}
                            data-testid={`invoice-resend-${channel}-btn`}
                          >
                            <Send className="h-3.5 w-3.5 mr-1" />
                            {resendingChannel === channel ? 'Senden…' : (attempted ? 'Erneut senden' : 'Senden')}
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Notes */}
              {selectedInvoice.notes && (
                <div>
                  <h3 className="font-semibold mb-2">Notizen</h3>
                  <p className="text-sm text-gray-600" data-testid={`invoice-detail-notes-${selectedInvoice.id}`}>{selectedInvoice.notes}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Zahlungsaufforderung Dialog (mit Buttons statt Prompt) */}
      <Dialog open={!!paymentRequestInvoice} onOpenChange={(open) => { if (!open) setPaymentRequestInvoice(null); }}>
        <DialogContent className="max-w-md" aria-describedby={undefined} data-testid="payment-request-dialog">
          <DialogHeader>
            <DialogTitle>💳 Zahlungsaufforderung senden</DialogTitle>
          </DialogHeader>
          {paymentRequestInvoice && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Wie soll der Payrexx-Zahlungslink für Rechnung{' '}
                <strong>{paymentRequestInvoice.invoice_number}</strong>{' '}
                (CHF {(paymentRequestInvoice.total || paymentRequestInvoice.total_amount || 0).toFixed(2)}) versendet werden?
              </p>
              <div className="grid grid-cols-1 gap-2">
                <Button
                  disabled={paymentRequestSending}
                  onClick={() => sendPaymentRequest('email')}
                  className="justify-start"
                  variant="outline"
                  data-testid="payment-request-email-btn"
                >
                  📧 Per Email senden
                </Button>
                <Button
                  disabled={paymentRequestSending}
                  onClick={() => sendPaymentRequest('whatsapp')}
                  className="justify-start"
                  variant="outline"
                  data-testid="payment-request-whatsapp-btn"
                >
                  💬 Per WhatsApp senden
                </Button>
                <Button
                  disabled={paymentRequestSending}
                  onClick={() => sendPaymentRequest('both')}
                  className="justify-start"
                  variant="outline"
                  data-testid="payment-request-both-btn"
                >
                  📧💬 Email + WhatsApp
                </Button>
                <Button
                  disabled={paymentRequestSending}
                  onClick={() => sendPaymentRequest('none')}
                  className="justify-start"
                  variant="outline"
                  data-testid="payment-request-link-only-btn"
                >
                  🔗 Nur Link erzeugen (kopieren)
                </Button>
              </div>
              {paymentRequestSending && (
                <p className="text-sm text-gray-500 animate-pulse">Wird gesendet…</p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Als bezahlt markieren — mit Auswahl der Zahlungsart */}
      <Dialog open={!!markPaidInvoice} onOpenChange={(open) => { if (!open) setMarkPaidInvoice(null); }}>
        <DialogContent className="max-w-md" aria-describedby={undefined} data-testid="mark-paid-dialog">
          <DialogHeader>
            <DialogTitle>Als bezahlt markieren</DialogTitle>
          </DialogHeader>
          {markPaidInvoice && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Rechnung <strong>{markPaidInvoice.invoice_number}</strong>{' '}
                (CHF {(markPaidInvoice.total || markPaidInvoice.total_amount || markPaidInvoice.amount || 0).toFixed(2)}).
                Wie hat der Kunde bezahlt?
              </p>
              <div>
                <label className="text-sm font-medium text-gray-700">Zahlungsart</label>
                <Select value={selectedPayMethod} onValueChange={setSelectedPayMethod}>
                  <SelectTrigger data-testid="mark-paid-method-select" className="mt-1">
                    <SelectValue placeholder="Zahlungsart wählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {payMethods.map((m) => (
                      <SelectItem key={m.key} value={m.key} data-testid={`mark-paid-method-${m.key}`}>
                        {m.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setMarkPaidInvoice(null)}
                  data-testid="mark-paid-cancel-btn"
                >
                  Abbrechen
                </Button>
                <Button
                  onClick={confirmMarkPaid}
                  disabled={markPaidSending || !selectedPayMethod}
                  className="bg-green-600 hover:bg-green-700"
                  data-testid="mark-paid-confirm-btn"
                >
                  {markPaidSending ? 'Speichern…' : 'Als bezahlt markieren'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <QrPayDialog
        invoice={qrInvoice}
        open={!!qrInvoice}
        onOpenChange={(o) => { if (!o) setQrInvoice(null); }}
        onPaid={() => { setQrInvoice(null); refreshInvoices(); }}
      />

      <SumUpPayDialog
        invoice={sumupInvoice}
        open={!!sumupInvoice}
        onOpenChange={(o) => { if (!o) setSumupInvoice(null); }}
        onPaid={() => { setSumupInvoice(null); refreshInvoices(); }}
      />
    </div>
  );
}
